import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Tooltip,
  DatePicker,
  Tag,
  Switch,
  Input,
  Spin,
  Popconfirm,
} from "antd";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  SkipForward,
  Mail,
  Zap,
  Users,
  ClipboardList,
  Cpu,
  RotateCcw,
  StickyNote,
} from "lucide-react";
import dayjs from "dayjs";
const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";
import type {
  PAPTemplate,
  PAPInstance,
  PAPTemplateItem,
  PAPInstanceItemState,
  PAPItemStatus,
} from "../types";

// ── helpers ────────────────────────────────────────────────────────────────────

const TYPE_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  round:           { icon: <Users className="h-3 w-3" />,       color: "#6366f1", label: "Round" },
  platform_action: { icon: <Cpu className="h-3 w-3" />,         color: "#0ea5e9", label: "Platform" },
  communication:   { icon: <Mail className="h-3 w-3" />,        color: "#f59e0b", label: "Comms" },
  meeting_agenda:  { icon: <ClipboardList className="h-3 w-3" />, color: "#10b981", label: "Agenda" },
  internal:        { icon: <StickyNote className="h-3 w-3" />,  color: "#94a3b8", label: "Internal" },
};

const PHASE_LABELS: Record<string, string> = {
  pre: "Pre-QBR",
  during: "During QBR",
  post: "Post-QBR",
};

function formatDate(iso: string): string {
  return dayjs(iso).format("MMM D");
}

function offsetLabel(offset: number): string {
  if (offset === 0) return "Day of";
  if (offset > 0) return `+${offset}d`;
  return `${offset}d`;
}

function resolvedAutomation(item: PAPTemplateItem, state: PAPInstanceItemState): "auto" | "manual" {
  return state.automation_override ?? item.automation;
}

// ── item row ───────────────────────────────────────────────────────────────────

function PlanItemRow({
  item,
  state,
  isPrivileged,
  onStatusChange,
  onAutomationToggle,
  onNotesChange,
}: {
  item: PAPTemplateItem;
  state: PAPInstanceItemState;
  isPrivileged: boolean;
  onStatusChange: (itemId: string, status: PAPItemStatus) => void;
  onAutomationToggle: (itemId: string, value: "auto" | "manual") => void;
  onNotesChange: (itemId: string, notes: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [localNotes, setLocalNotes] = useState(state.notes || "");
  const meta = TYPE_META[item.type] ?? TYPE_META.internal;
  const auto = resolvedAutomation(item, state);
  const isDone = state.status === "done";
  const isSkipped = state.status === "skipped";

  return (
    <div
      style={{
        borderBottom: "1px solid #f1f5f9",
        opacity: isSkipped ? 0.45 : 1,
      }}
    >
      {/* Main row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          cursor: "pointer",
          background: isDone ? "#f8fffe" : "white",
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        {/* Status icon */}
        <div style={{ flexShrink: 0, width: 16 }}>
          {isDone ? (
            <CheckCircle2 className="h-4 w-4" style={{ color: "#10b981" }} />
          ) : isSkipped ? (
            <SkipForward className="h-3.5 w-3.5" style={{ color: "#94a3b8" }} />
          ) : (
            <Circle className="h-4 w-4" style={{ color: "#cbd5e1" }} />
          )}
        </div>

        {/* Type badge */}
        <Tooltip title={meta.label}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              color: meta.color,
              flexShrink: 0,
            }}
          >
            {meta.icon}
          </span>
        </Tooltip>

        {/* Title */}
        <span
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: 500,
            color: isDone ? "#64748b" : "#1e293b",
            textDecoration: isSkipped ? "line-through" : "none",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.title}
        </span>

        {/* Scheduled date */}
        {state.scheduled_date && (
          <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>
            {formatDate(state.scheduled_date)}
          </span>
        )}

        {/* Auto pill */}
        {auto === "auto" && !isDone && !isSkipped && (
          <Tooltip title="Will fire automatically">
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                fontSize: 10,
                color: "#6366f1",
                background: "#eef2ff",
                borderRadius: 4,
                padding: "1px 5px",
                flexShrink: 0,
              }}
            >
              <Zap className="h-2.5 w-2.5" />
              Auto
            </span>
          </Tooltip>
        )}

        {/* Chevron */}
        <span style={{ color: "#cbd5e1", flexShrink: 0 }}>
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5" />
            : <ChevronRight className="h-3.5 w-3.5" />
          }
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div
          style={{
            background: "#f8fafc",
            padding: "10px 12px 12px 36px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Description */}
          {item.description && (
            <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>
              {item.description}
            </p>
          )}

          {/* Meta row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Tag style={{ fontSize: 10 }}>
              {item.assigned_role === "advisor" ? "Advisor" : item.assigned_role === "owner" ? "Owner" : "Team"}
            </Tag>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>
              {state.scheduled_date ? formatDate(state.scheduled_date) : `${offsetLabel(item.offset_days)} from anchor`}
            </span>
            {state.fired_at && (
              <Tag color="success" style={{ fontSize: 10 }}>
                Auto-fired {formatDate(state.fired_at)}
              </Tag>
            )}
          </div>

          {isPrivileged && (
            <>
              {/* Automation toggle (only for auto-capable types) */}
              {item.type !== "meeting_agenda" && item.type !== "internal" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#64748b" }}>Automation:</span>
                  <Switch
                    size="small"
                    checked={auto === "auto"}
                    onChange={(checked) =>
                      onAutomationToggle(item.id, checked ? "auto" : "manual")
                    }
                    checkedChildren={<span style={{ fontSize: 9 }}>Auto</span>}
                    unCheckedChildren={<span style={{ fontSize: 9 }}>Manual</span>}
                    style={{ backgroundColor: auto === "auto" ? "#6366f1" : undefined }}
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <p style={{ fontSize: 10, color: "#94a3b8", margin: "0 0 4px" }}>Advisor notes</p>
                <Input.TextArea
                  value={localNotes}
                  onChange={(e) => setLocalNotes(e.target.value)}
                  onBlur={() => onNotesChange(item.id, localNotes)}
                  rows={2}
                  style={{ fontSize: 11 }}
                  placeholder="Add context or notes…"
                />
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 6 }}>
                {!isDone && !isSkipped && (
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckCircle2 className="h-3 w-3" />}
                    style={{ fontSize: 11, background: "#10b981", borderColor: "#10b981" }}
                    onClick={() => onStatusChange(item.id, "done")}
                  >
                    Mark Done
                  </Button>
                )}
                {isDone && (
                  <Button
                    size="small"
                    icon={<RotateCcw className="h-3 w-3" />}
                    style={{ fontSize: 11 }}
                    onClick={() => onStatusChange(item.id, "pending")}
                  >
                    Reopen
                  </Button>
                )}
                {!isSkipped && !isDone && (
                  <Popconfirm
                    title="Skip this item?"
                    onConfirm={() => onStatusChange(item.id, "skipped")}
                    okText="Skip"
                    cancelText="Cancel"
                  >
                    <Button size="small" type="text" style={{ fontSize: 11, color: "#94a3b8" }}>
                      Skip
                    </Button>
                  </Popconfirm>
                )}
                {isSkipped && (
                  <Button
                    size="small"
                    type="text"
                    style={{ fontSize: 11 }}
                    onClick={() => onStatusChange(item.id, "pending")}
                  >
                    Restore
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── phase section ──────────────────────────────────────────────────────────────

function PhaseSection({
  phase,
  items,
  states,
  anchorDate,
  isPrivileged,
  onStatusChange,
  onAutomationToggle,
  onNotesChange,
}: {
  phase: "pre" | "during" | "post";
  items: PAPTemplateItem[];
  states: Record<string, PAPInstanceItemState>;
  anchorDate: string;
  isPrivileged: boolean;
  onStatusChange: (itemId: string, status: PAPItemStatus) => void;
  onAutomationToggle: (itemId: string, value: "auto" | "manual") => void;
  onNotesChange: (itemId: string, notes: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const phaseItems = items.filter((it) => it.phase === phase).sort((a, b) => a.sort_order - b.sort_order);
  const doneCount = phaseItems.filter((it) => states[it.id]?.status === "done").length;

  return (
    <div style={{ borderBottom: "1px solid #e2e8f0" }}>
      {/* Phase header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "#f8fafc",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
          <span style={{ fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {PHASE_LABELS[phase]}
          </span>
          {phase === "during" && anchorDate && (
            <span style={{ fontSize: 10, color: "#94a3b8" }}>· {dayjs(anchorDate).format("MMM D, YYYY")}</span>
          )}
        </div>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>
          {doneCount}/{phaseItems.length}
        </span>
      </div>

      {/* Items */}
      {!collapsed && phaseItems.map((item) => (
        <PlanItemRow
          key={item.id}
          item={item}
          state={states[item.id] ?? {
            template_item_id: item.id,
            scheduled_date: "",
            status: "pending",
            automation_override: null,
            fired_at: null,
            linked_artifact: null,
            notes: "",
            completed_at: null,
          }}
          isPrivileged={isPrivileged}
          onStatusChange={onStatusChange}
          onAutomationToggle={onAutomationToggle}
          onNotesChange={onNotesChange}
        />
      ))}
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

interface PAPPlanViewProps {
  sessionId: string;
  periodType: "qbr" | "weekly" | "annual";
  isAdvisorOrOwner: boolean;
}

export function PAPPlanView({ sessionId, periodType, isAdvisorOrOwner }: PAPPlanViewProps) {
  const [template, setTemplate] = useState<PAPTemplate | null>(null);
  const [instance, setInstance] = useState<PAPInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingDate, setSettingDate] = useState(false);
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load template + active instance.
  // Uses a no-cors warm-up request first to absorb any stale TCP connections
  // in Chrome's pool (which happen when uvicorn reloads in dev and drops existing connections).
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Absorb any stale connection before making credentialed cross-origin requests.
    // no-cors mode bypasses CORS checks so a stale-connection failure doesn't block us.
    try { await fetch(`${SERVER_URL}/`, { mode: "no-cors" }); } catch { /* absorbed */ }
    try {
      const [tmplRes, instRes] = await Promise.all([
        fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/pap-template/${periodType}`, { credentials: "include" }),
        fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/pap-instances/active?period_type=${periodType}`, { credentials: "include" }),
      ]);
      if (tmplRes.ok) {
        setTemplate(await tmplRes.json());
      } else {
        const body = await tmplRes.json().catch(() => ({}));
        setError(`Failed to load plan (${tmplRes.status}): ${body.detail ?? "unknown error"}`);
      }
      if (instRes.ok) {
        const inst = await instRes.json();
        setInstance(inst);
      } else {
        setInstance(null);
      }
    } catch (e) {
      console.error("[PAPPlanView] load error", e);
      setError("Network error — click Retry or reload the page.");
    }
    finally { setLoading(false); }
  }, [sessionId, periodType]);

  useEffect(() => { load(); }, [load]);

  // Build a quick lookup: template_item_id → state
  const stateMap: Record<string, PAPInstanceItemState> = {};
  if (instance) {
    for (const s of instance.item_states) {
      stateMap[s.template_item_id] = s;
    }
  }

  const handleCreateInstance = async () => {
    if (!pendingDate) {
      setError("Please pick a date first.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const periodKey = periodType === "qbr"
        ? `Q${Math.ceil((dayjs(pendingDate).month() + 1) / 3)}-${dayjs(pendingDate).year()}`
        : dayjs(pendingDate).format("YYYY-[W]WW");

      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/pap-instances`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ period_type: periodType, period_key: periodKey, anchor_date: pendingDate }),
      });
      if (r.ok) {
        setSettingDate(false);
        setPendingDate(null);
        // Reload both template and instance — template may not have loaded if
        // the initial fetch failed due to a stale TCP connection.
        load();
      } else {
        const body = await r.json().catch(() => ({}));
        const msg = `Failed to activate plan (${r.status}): ${body.detail ?? "unknown error"}`;
        setError(msg);
        console.error("[PAPPlanView] createInstance error", r.status, body);
      }
    } catch (e) {
      console.error("[PAPPlanView] createInstance network error", e);
      setError("Network error. Please try again.");
    }
    finally { setSaving(false); }
  };

  const handleUpdateAnchorDate = async (newDate: string) => {
    if (!instance) return;
    setSaving(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/pap-instances/${instance.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anchor_date: newDate }),
      });
      if (r.ok) setInstance(await r.json());
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleAutomationPause = async (paused: boolean) => {
    if (!instance) return;
    const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/pap-instances/${instance.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ automation_paused: paused }),
    });
    if (r.ok) setInstance(await r.json());
  };

  const handleStatusChange = async (itemId: string, status: PAPItemStatus) => {
    if (!instance) return;
    const r = await fetch(
      `${SERVER_URL}/api/rb/sessions/${sessionId}/pap-instances/${instance.id}/items/${itemId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      }
    );
    if (r.ok) setInstance(await r.json());
  };

  const handleAutomationToggle = async (itemId: string, value: "auto" | "manual") => {
    if (!instance) return;
    const r = await fetch(
      `${SERVER_URL}/api/rb/sessions/${sessionId}/pap-instances/${instance.id}/items/${itemId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ automation_override: value }),
      }
    );
    if (r.ok) setInstance(await r.json());
  };

  const handleNotesChange = async (itemId: string, notes: string) => {
    if (!instance) return;
    await fetch(
      `${SERVER_URL}/api/rb/sessions/${sessionId}/pap-instances/${instance.id}/items/${itemId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      }
    );
  };

  const totalItems = template?.items.length ?? 0;
  const doneItems = instance?.item_states.filter((s) => s.status === "done").length ?? 0;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Error banner */}
      {error && (
        <div style={{ padding: "6px 12px", background: "#fef2f2", borderBottom: "1px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: "#ef4444" }}>{error}</span>
          <Button size="small" type="link" style={{ fontSize: 11, padding: 0, color: "#ef4444" }} onClick={() => load()}>Retry</Button>
        </div>
      )}
      {/* Plan header */}
      <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid #e2e8f0", background: "white", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Calendar className="h-3.5 w-3.5" style={{ color: "#2c1561" }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
              {periodType === "qbr" ? "QBR Plan" : periodType === "weekly" ? "Weekly Plan" : "Annual Plan"}
              {instance?.period_key && (
                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6 }}>· {instance.period_key}</span>
              )}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Progress count */}
            {instance && (
              <span style={{ fontSize: 10, color: "#94a3b8" }}>{doneItems}/{totalItems} done</span>
            )}

            {/* Automation master toggle — Advisor/Owner only */}
            {isAdvisorOrOwner && instance && (
              <Tooltip title={instance.automation_paused ? "Automation paused — click to resume" : "Automation running — click to pause"}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Zap className="h-3 w-3" style={{ color: instance.automation_paused ? "#94a3b8" : "#6366f1" }} />
                  <Switch
                    size="small"
                    checked={!instance.automation_paused}
                    onChange={(checked) => handleAutomationPause(!checked)}
                    style={{ backgroundColor: !instance.automation_paused ? "#6366f1" : undefined }}
                  />
                </div>
              </Tooltip>
            )}

            {/* Anchor date */}
            {isAdvisorOrOwner && (
              instance ? (
                <Tooltip title="Change QBR date (all item dates will recalculate)">
                  <DatePicker
                    size="small"
                    value={dayjs(instance.anchor_date)}
                    format="MMM D, YYYY"
                    style={{ width: 120, fontSize: 11 }}
                    onChange={(date) => { if (date) handleUpdateAnchorDate(date.format("YYYY-MM-DD")); }}
                    allowClear={false}
                    suffixIcon={<Calendar className="h-3 w-3" />}
                  />
                </Tooltip>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  icon={<Calendar className="h-3 w-3" />}
                  style={{ fontSize: 11, background: "#2c1561", borderColor: "#2c1561" }}
                  onClick={() => setSettingDate(true)}
                >
                  Set {periodType === "qbr" ? "QBR" : "Meeting"} Date
                </Button>
              )
            )}
          </div>
        </div>

        {/* No-instance prompt */}
        {!instance && !settingDate && (
          <p style={{ fontSize: 11, color: "#94a3b8", margin: "6px 0 0" }}>
            {isAdvisorOrOwner
              ? "Set the QBR date to activate the plan — all items will auto-schedule from that anchor."
              : "The Advisor has not yet set a QBR date for this cycle."}
          </p>
        )}

        {/* Date picker for new instance */}
        {settingDate && isAdvisorOrOwner && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <DatePicker
                size="small"
                format="MMM D, YYYY"
                style={{ flex: 1 }}
                onChange={(date) => {
                  setPendingDate(date ? date.format("YYYY-MM-DD") : null);
                  setError(null);
                }}
                allowClear={false}
              />
              <Button
                size="small"
                type="primary"
                loading={saving}
                style={{ background: "#2c1561", borderColor: "#2c1561" }}
                onClick={handleCreateInstance}
              >
                Activate
              </Button>
              <Button size="small" onClick={() => { setSettingDate(false); setPendingDate(null); setError(null); }}>
                Cancel
              </Button>
            </div>
            {error && (
              <p style={{ fontSize: 11, color: "#ef4444", margin: "4px 0 0" }}>{error}</p>
            )}
          </div>
        )}
      </div>

      {/* Phase sections */}
      {template && instance && (
        <div style={{ flex: 1, overflowY: "auto" }}>
          {(["pre", "during", "post"] as const).map((phase) => (
            <PhaseSection
              key={phase}
              phase={phase}
              items={template.items}
              states={stateMap}
              anchorDate={instance.anchor_date}
              isPrivileged={isAdvisorOrOwner}
              onStatusChange={handleStatusChange}
              onAutomationToggle={handleAutomationToggle}
              onNotesChange={handleNotesChange}
            />
          ))}
        </div>
      )}

      {/* Template visible but no instance yet */}
      {template && !instance && (
        <div style={{ flex: 1, overflowY: "auto", opacity: 0.4, pointerEvents: "none" }}>
          {(["pre", "during", "post"] as const).map((phase) => (
            <PhaseSection
              key={phase}
              phase={phase}
              items={template.items}
              states={{}}
              anchorDate=""
              isPrivileged={false}
              onStatusChange={() => {}}
              onAutomationToggle={() => {}}
              onNotesChange={() => {}}
            />
          ))}
        </div>
      )}
    </div>
  );
}
